import { ConsultantTableColumns } from "@bonliva-crm/shared";
import { useMemo, useState } from "react";
import locale from "@bonliva-crm/locale";
import { uniq } from "lodash";

export const defaultColumns: ConsultantTableColumns[] = [
  ConsultantTableColumns.Name,
  ConsultantTableColumns.Phone,
  ConsultantTableColumns.Email,
  ConsultantTableColumns.SocialSecurityNo,
  ConsultantTableColumns.Availability,
  ConsultantTableColumns.LastContactDate,
  ConsultantTableColumns.AreasOfExpertise,
  ConsultantTableColumns.ResponsiblePerson
];

export const qualityColumns: ConsultantTableColumns[] = [
  ConsultantTableColumns.IsQualityApproved,
  ConsultantTableColumns.IsNationalQualityApproved,
  ConsultantTableColumns.IVO,
  ConsultantTableColumns.IVOErrands,
  ConsultantTableColumns.HOSP,
  ConsultantTableColumns.SwedenProofOfWork,
  ConsultantTableColumns.SwedenProofOfEducation,
  ConsultantTableColumns.SwedenCVAudit,
  ConsultantTableColumns.SwedenReferences,
  ConsultantTableColumns.SwedenNationalReferences,
  ConsultantTableColumns.SwedenCriminalRecord,
  ConsultantTableColumns.SwedenNpCriminalRecord,
  ConsultantTableColumns.SwedenHealthDeclaration,
  ConsultantTableColumns.SwedenSAHLR,
  ConsultantTableColumns.SwedenIdCheck,
  ConsultantTableColumns.SwedenInterviewDone,
  ConsultantTableColumns.SwedenLanguageCheck,
  ConsultantTableColumns.SwedenHSAIDSITHS,
  ConsultantTableColumns.QualityNote
];

type Params = {
  name: string;
  localeKey: string;
  defaultColumns: (number | string)[];
  altPresetColumns?: (number | string)[];
};

export type ColumnNameSelector = {
  localStorageName: string;
  defaultColumns: { label: string; value: string | number }[];
};

export type ColumnNameSelectorHook = {
  columnNameSelector: ColumnNameSelector;
  toggleAlt: () => void;
  isAlt: boolean;
};

export function useColumnNameSelector({
  name,
  defaultColumns,
  localeKey,
  altPresetColumns
}: Params): ColumnNameSelectorHook {
  const [isAlt, setAlt] = useState(
    () => localStorage.getItem(`${name}-alt-active`) === "true"
  );

  const toggleAlt = () => {
    localStorage.setItem(`${name}-alt-active`, isAlt ? "false" : "true");
    setAlt(!isAlt);
  };

  const columnNameSelector = useMemo(() => {
    if (isAlt) {
      const _defaultColumns = JSON.parse(localStorage.getItem(name)) || defaultColumns;
      return {
        localStorageName: `${name}-alt`,
        defaultColumns: uniq([..._defaultColumns, ...altPresetColumns]).map((value) => ({
          label: locale[localeKey][value],
          value: value
        }))
      };
    } else {
      return {
        localStorageName: name,
        defaultColumns: defaultColumns.map((value) => ({
          label: locale[localeKey][value],
          value: value
        }))
      };
    }
  }, [altPresetColumns, defaultColumns, isAlt, localeKey, name]);

  return { columnNameSelector, toggleAlt, isAlt };
}
